import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Plus, Trash2, GripVertical } from 'lucide-react';

const pollTypes = [
  { id: 'multiple_choice', name: 'Multiple Choice', desc: 'Single selection' },
  { id: 'multiple_answer', name: 'Multiple Answer', desc: 'Multiple selections' },
  { id: 'yes_no', name: 'Yes/No', desc: 'Simple binary' },
  { id: 'rating', name: 'Rating', desc: 'Scale rating' },
  { id: 'open_text', name: 'Open Text', desc: 'Free response' },
];

const positions = ['bottom_center', 'bottom_left', 'bottom_right', 'side_panel', 'fullscreen'];

// Custom Select component - uses portal to avoid overflow clipping
const Select = ({ value, onValueChange, children, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current && !selectRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recalculate position when dropdown opens or window scrolls/resizes
  useEffect(() => {
    if (!isOpen || !selectRef.current) return;

    const updatePosition = () => {
      const rect = selectRef.current.getBoundingClientRect();
      const dropdownHeight = 240; // max-h-60 = 15rem = 240px
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;

      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        zIndex: 99999,
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const options = React.Children.toArray(children);
  const selectedOption = options.find(opt => opt.props.value === value);

  return (
    <div className="relative" ref={selectRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors"
      >
        <span className={selectedOption ? '' : 'text-gray-500'}>
          {selectedOption ? selectedOption.props.children : placeholder}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto"
        >
          {options.map((option) => (
            <button
              key={option.props.value}
              type="button"
              onClick={() => {
                onValueChange(option.props.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.props.value === value ? 'text-orange-400' : 'text-gray-300'
              }`}
            >
              {option.props.children}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const SelectItem = ({ value, children }) => null;

// Custom Switch component
const Switch = ({ checked, onCheckedChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onCheckedChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Custom Slider component
const Slider = ({ value, onValueChange, min, max, step }) => {
  const percentage = ((value[0] - min) / (max - min)) * 100;
  return (
    <div className="relative h-6 flex items-center">
      <div className="w-full h-2 bg-white/20 rounded-full">
        <div
          className="h-full bg-[#6a1fbf] rounded-full"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[0]}
        onChange={(e) => onValueChange([parseInt(e.target.value)])}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      <div
        className="absolute w-4 h-4 bg-white rounded-full shadow-md pointer-events-none"
        style={{ left: `calc(${percentage}% - 8px)` }}
      />
    </div>
  );
};

// Tabs components
const Tabs = ({ defaultValue, children, className }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);
  return (
    <div className={className}>
      {React.Children.map(children, child => {
        if (child.type === TabsList) {
          return React.cloneElement(child, { activeTab, setActiveTab });
        }
        if (child.type === TabsContent) {
          return child.props.value === activeTab ? child : null;
        }
        return child;
      })}
    </div>
  );
};

const TabsList = ({ children, className, activeTab, setActiveTab }) => (
  <div className={`flex ${className}`}>
    {React.Children.map(children, child =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ value, children, activeTab, setActiveTab }) => (
  <button
    type="button"
    onClick={() => setActiveTab(value)}
    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg ${
      activeTab === value
        ? 'bg-white/10 text-white'
        : 'text-gray-400 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const TabsContent = ({ children, className }) => (
  <div className={className}>{children}</div>
);

export default function PollModal({ isOpen, onClose, onSave, poll, folders, currentFolder, skipFolderSelect = false, readOnly = false, onCopyToMyElements }) {
  const [formData, setFormData] = useState({
    folder_id: currentFolder?.id || '',
    name: '',
    question: '',
    poll_type: 'multiple_choice',
    options: [
      { text: '', image_url: '', color: '#6a1fbf', votes: 0 },
      { text: '', image_url: '', color: '#00c853', votes: 0 },
    ],
    rating_settings: { scale_type: 'stars', max_value: 5, min_label: 'Poor', max_label: 'Excellent' },
    open_text_settings: { character_limit: 200, moderation: false, anonymous: true },
    appearance: { position: 'bottom_center', size: 'medium', background_color: '#001233ee', text_color: '#ffffff', accent_color: '#6a1fbf', border_enabled: true, animation: 'fade' },
    duration: { always_visible: true, seconds: 60, show_countdown: true },
    results_settings: { show_immediately: true, show_after_voting: false, hide_until_closed: false, display_type: 'bar', anonymous_voting: true, correct_answer_index: -1 },
    status: 'draft',
    total_votes: 0,
    votes: []
  });
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize form data only when modal opens, not on every prop change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (poll) {
        setFormData(prev => ({ ...prev, ...poll }));
      } else {
        setFormData({
          folder_id: currentFolder?.id || '',
          name: '',
          question: '',
          poll_type: 'multiple_choice',
          options: [
            { text: '', image_url: '', color: '#6a1fbf', votes: 0 },
            { text: '', image_url: '', color: '#00c853', votes: 0 },
          ],
          rating_settings: { scale_type: 'stars', max_value: 5, min_label: 'Poor', max_label: 'Excellent' },
          open_text_settings: { character_limit: 200, moderation: false, anonymous: true },
          appearance: { position: 'bottom_center', size: 'medium', background_color: '#001233ee', text_color: '#ffffff', accent_color: '#6a1fbf', border_enabled: true, animation: 'fade' },
          duration: { always_visible: true, seconds: 60, show_countdown: true },
          results_settings: { show_immediately: true, show_after_voting: false, hide_until_closed: false, display_type: 'bar', anonymous_voting: true, correct_answer_index: -1 },
          status: 'draft',
          total_votes: 0,
          votes: []
        });
      }
      setHasInitialized(true);
    }
  }, [isOpen, poll, currentFolder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const updateField = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addOption = () => {
    if (formData.options.length >= 10) return;
    const colors = ['#6a1fbf', '#00c853', '#ff6b6b', '#ffd93d', '#4ecdc4', '#ff9f43', '#a55eea', '#45b7d1', '#96ceb4', '#ff85a2'];
    updateField('options', [
      ...formData.options,
      { text: '', image_url: '', color: colors[formData.options.length % colors.length], votes: 0 }
    ]);
  };

  const removeOption = (index) => {
    if (formData.options.length <= 2) return;
    updateField('options', formData.options.filter((_, i) => i !== index));
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    updateField('options', newOptions);
  };

  const handleSave = async () => {
    await onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-xl font-bold text-white">
              {poll ? 'Edit Poll' : 'Create Poll'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {readOnly && (
              <div className="flex items-start gap-3 mx-6 mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-sm text-purple-200">
                  This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                </p>
              </div>
            )}
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className={skipFolderSelect ? '' : 'grid grid-cols-2 gap-4'}>
                  <div>
                    <label className="text-gray-400 mb-2 block text-sm">Poll Name (internal)</label>
                    <input
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="e.g., Feature Poll 1"
                      disabled={readOnly}
                      className={`w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                  </div>
                  {!skipFolderSelect && (
                    <div>
                      <label className="text-gray-400 mb-2 block text-sm">Folder</label>
                      <Select value={formData.folder_id} onValueChange={(v) => updateField('folder_id', v)} placeholder="Select folder...">
                        {folders?.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                      </Select>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-gray-400 mb-2 block text-sm">Question</label>
                  <textarea
                    value={formData.question}
                    onChange={(e) => updateField('question', e.target.value)}
                    placeholder="What would you like to ask your audience?"
                    disabled={readOnly}
                    className={`w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors h-20 resize-none ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>

              {/* Poll Type */}
              <div>
                <label className="text-gray-400 mb-3 block text-sm">Poll Type</label>
                <div className="grid grid-cols-5 gap-2">
                  {pollTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => updateField('poll_type', type.id)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        formData.poll_type === type.id
                          ? 'bg-[#6a1fbf] ring-2 ring-[#6a1fbf]'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <p className="text-sm font-medium text-white">{type.name}</p>
                      <p className="text-[10px] text-gray-400">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Options (for multiple choice types) */}
              {['multiple_choice', 'multiple_answer'].includes(formData.poll_type) && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-gray-400 text-sm">Options</label>
                    <button
                      type="button"
                      onClick={addOption}
                      disabled={formData.options.length >= 10}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm border border-white/20 rounded-lg text-white hover:bg-white/10 transition-colors disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" />
                      Add Option
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <GripVertical className="w-4 h-4 text-gray-600 cursor-grab" />
                        <input
                          type="color"
                          value={option.color}
                          onChange={(e) => updateOption(index, 'color', e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border-0"
                        />
                        <input
                          value={option.text}
                          onChange={(e) => updateOption(index, 'text', e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
                        />
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          disabled={formData.options.length <= 2}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-30"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rating Settings */}
              {formData.poll_type === 'rating' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 mb-2 block text-sm">Scale Type</label>
                      <Select
                        value={formData.rating_settings.scale_type}
                        onValueChange={(v) => {
                          updateField('rating_settings.scale_type', v);
                          // Update max_value based on scale type
                          updateField('rating_settings.max_value', v === 'numbers' ? 10 : 5);
                        }}
                        placeholder="Select scale..."
                      >
                        <SelectItem value="stars">Stars (1-5)</SelectItem>
                        <SelectItem value="numbers">Numbers (1-10)</SelectItem>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      value={formData.rating_settings.min_label}
                      onChange={(e) => updateField('rating_settings.min_label', e.target.value)}
                      placeholder="Min label (e.g., Poor)"
                      className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
                    />
                    <input
                      value={formData.rating_settings.max_label}
                      onChange={(e) => updateField('rating_settings.max_label', e.target.value)}
                      placeholder="Max label (e.g., Excellent)"
                      className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Display Settings */}
              <Tabs defaultValue="appearance" className="w-full">
                <TabsList className="grid grid-cols-3 w-full bg-white/5 rounded-lg p-1">
                  <TabsTrigger value="appearance">Appearance</TabsTrigger>
                  <TabsTrigger value="duration">Duration</TabsTrigger>
                  <TabsTrigger value="results">Results</TabsTrigger>
                </TabsList>

                <TabsContent value="appearance" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Position</label>
                      <Select
                        value={formData.appearance.position}
                        onValueChange={(v) => updateField('appearance.position', v)}
                        placeholder="Select position..."
                      >
                        {positions.map(p => (
                          <SelectItem key={p} value={p}>
                            {p.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Size</label>
                      <Select
                        value={formData.appearance.size}
                        onValueChange={(v) => updateField('appearance.size', v)}
                        placeholder="Select size..."
                      >
                        <SelectItem value="small">Small</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="large">Large</SelectItem>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="text-xs text-gray-400 mb-1 block">Accent Color</label>
                      <input
                        type="color"
                        value={formData.appearance.accent_color}
                        onChange={(e) => updateField('appearance.accent_color', e.target.value)}
                        className="w-full h-10 rounded cursor-pointer border-0"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-400 mb-1 block">Animation</label>
                      <Select
                        value={formData.appearance.animation}
                        onValueChange={(v) => updateField('appearance.animation', v)}
                        placeholder="Select animation..."
                      >
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="fade">Fade</SelectItem>
                        <SelectItem value="slide">Slide</SelectItem>
                        <SelectItem value="zoom">Zoom</SelectItem>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="duration" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <p className="text-white font-medium">Always Visible</p>
                      <p className="text-xs text-gray-400">Poll stays until closed</p>
                    </div>
                    <Switch
                      checked={formData.duration.always_visible}
                      onCheckedChange={(v) => updateField('duration.always_visible', v)}
                    />
                  </div>
                  {!formData.duration.always_visible && (
                    <>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">
                          Duration: {formData.duration.seconds} seconds
                        </label>
                        <Slider
                          value={[formData.duration.seconds]}
                          onValueChange={([v]) => updateField('duration.seconds', v)}
                          min={30}
                          max={600}
                          step={30}
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                        <span className="text-white">Show Countdown</span>
                        <Switch
                          checked={formData.duration.show_countdown}
                          onCheckedChange={(v) => updateField('duration.show_countdown', v)}
                        />
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="results" className="space-y-4 mt-4">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Results Display</label>
                    <Select
                      value={formData.results_settings.display_type}
                      onValueChange={(v) => updateField('results_settings.display_type', v)}
                      placeholder="Select display type..."
                    >
                      <SelectItem value="bar">Bar Chart</SelectItem>
                      <SelectItem value="pie">Pie Chart</SelectItem>
                      <SelectItem value="percentage">Percentages</SelectItem>
                      <SelectItem value="count">Vote Counts</SelectItem>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                      <span className="text-sm text-white">Show Results Immediately</span>
                      <Switch
                        checked={formData.results_settings.show_immediately}
                        onCheckedChange={(v) => updateField('results_settings.show_immediately', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                      <span className="text-sm text-white">Anonymous Voting</span>
                      <Switch
                        checked={formData.results_settings.anonymous_voting}
                        onCheckedChange={(v) => updateField('results_settings.anonymous_voting', v)}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={!formData.name.trim() || !formData.question.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {poll ? 'Save Changes' : 'Create Poll'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
